// src\themes\choopar\components\StatCounter.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'motion/react';

type StatItem = {
    title?: string;      // عدد، مثلا "500" یا "20"
    subtitle?: string;   // پسوند، مثلا "+" یا "%"
    description?: string; // برچسب، مثلا "محصول فروخته‌شده"
};

type StatCounterData = {
    title?: string;
    description?: string;
    items?: StatItem[];
};

function Counter({ value }: { value: number }) {
    const ref = useRef<HTMLSpanElement>(null);
    const motionValue = useMotionValue(0);
    const springValue = useSpring(motionValue, { damping: 26, stiffness: 60 });
    const isInView = useInView(ref, { once: true, margin: '-10% 0px -10% 0px' });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (isInView) motionValue.set(value);
    }, [isInView, value, motionValue]);

    useEffect(() => {
        const unsubscribe = springValue.on('change', latest => {
            setDisplay(Math.round(latest));
        });
        return unsubscribe;
    }, [springValue]);

    return <span ref={ref}>{display.toLocaleString('fa-IR')}</span>;
}

export default function StatCounter({ data }: { data: StatCounterData }) {
    const items = data.items || [];
    if (!items.length) return null;

    return (
        <div className="col-span-12">
            {(data.title || data.description) && (
                <div className="text-center mb-12 space-y-3">
                    {data.title && (
                        <h2 className="text-xl lg:text-2xl font-light text-primary-900 tracking-tight">
                            {data.title}
                        </h2>
                    )}
                    {data.description && (
                        <p className="text-[13px] text-primary-500 max-w-md mx-auto">
                            {data.description}
                        </p>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-primary-200 rtl:divide-x-reverse border-y border-primary-900">
                {items.map((item, index) => {
                    const numeric = parseFloat(item.title || '0') || 0;
                    return (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="flex flex-col items-center justify-center text-center py-10 px-3"
                        >
                            <span className="text-3xl lg:text-5xl font-primary-bold text-primary-900 tracking-tight">
                                <Counter value={numeric} />
                                {item.subtitle}
                            </span>
                            {item.description && (
                                <span className="mt-3 text-[11px] uppercase tracking-[0.12em] text-primary-500">
                                    {item.description}
                                </span>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}